import React from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import Snackbar from '~/components/Snackbar';
import useUserGroups from '~/data/hooks/useUserGroups';
import { TeamDetail, TeamUser } from '~/data/models/team';
import { ACCEPT_USER_IN_TEAM, TEAM_DETAIL } from '~/data/operations/team';
import { Status } from '~/data/types/graphql';
import { PlanningStackParamList } from '~/navigation/types';
import AddTeamMemberLayout from './layout';

type AddTeamMemberNavProp = StackNavigationProp<
  PlanningStackParamList,
  'AddTeamMember'
>;
type AddTeamMemberRouteProp = RouteProp<
  PlanningStackParamList,
  'AddTeamMember'
>;
export default function AddTeamMember(): JSX.Element {
  const navigation = useNavigation<AddTeamMemberNavProp>();
  const {
    params: { teamId, localIncludedUserIds, onAddUser },
  } = useRoute<AddTeamMemberRouteProp>();

  const {
    userGroups,
    loading: userGroupsLoading,
    onRefresh: onRefetchUserGroups,
    onLoadMore: onLoadMoreUsers,
  } = useUserGroups();

  const {
    data: teamData,
    loading: loadingTeam,
    refetch: refetchTeam,
  } = useQuery(TEAM_DETAIL, {
    skip: !teamId,
    variables: {
      id: teamId as string,
    },
    pollInterval: 10000,
  });

  const team = teamData?.team as TeamDetail;
  const teamUsers: TeamUser[] =
    (team?.users?.map((user) => user) as TeamUser[]) || [];

  const includedUserIds = teamUsers.map((teamUser) => teamUser.user.id);

  const pendingTeamUsers: TeamUser[] = teamUsers.filter(
    (teamUser) => teamUser.status === Status.PENDING,
  );

  const [acceptUserInTeam] = useMutation(ACCEPT_USER_IN_TEAM);

  const onAcceptUserInTeam = async (userId: string, accept: boolean) => {
    try {
      if (teamId) {
        await acceptUserInTeam({
          variables: {
            input: {
              teamId,
              userId,
              accept,
            },
          },
        });
      }
    } catch (e) {
      if (e instanceof Error) {
        Snackbar.show(e.message);
      }
    }
  };

  return (
    <AddTeamMemberLayout
      userGroups={userGroups}
      teamUsers={pendingTeamUsers}
      includedUserIds={localIncludedUserIds || includedUserIds}
      team={team}
      loading={loadingTeam}
      loadingMore={userGroupsLoading}
      onBack={() => navigation.goBack()}
      onSelectedUser={(userId) => {
        onAddUser(userId);
        navigation.goBack();
      }}
      onDeclineUser={(userId) => onAcceptUserInTeam(userId, false)}
      onAcceptUser={(userId) => onAcceptUserInTeam(userId, true)}
      onRefresh={() => {
        refetchTeam();
        onRefetchUserGroups();
      }}
      onLoadMoreUsers={onLoadMoreUsers}
    />
  );
}
